import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Laptop, Smartphone, Tablet } from "lucide-react";
import { ApiError, type CompanyDeviceDto } from "@finora/api-client";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

function platformLabel(p: string) {
  if (p === "WINDOWS") return "Kasa na računaru (Windows)";
  if (p === "LINUX") return "Kasa na računaru (Linux)";
  if (p === "MACOS") return "Kasa na računaru (Mac)";
  if (p === "IOS") return "iPhone / iPad";
  return "Android telefon ili tablet";
}

function PlatformIcon({ p }: { p: string }) {
  const cls = "h-5 w-5 text-slate-500";
  if (p === "WINDOWS" || p === "LINUX" || p === "MACOS") return <Laptop className={cls} aria-hidden />;
  if (p === "IOS") return <Tablet className={cls} aria-hidden />;
  return <Smartphone className={cls} aria-hidden />;
}

function lastSeen(iso: string) {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 2) return "upravo sada";
  if (mins < 60) return `pre ${mins} min`;
  if (mins < 60 * 24) return `pre ${Math.round(mins / 60)} h`;
  return new Date(iso).toLocaleDateString("sr-RS");
}

const LPFR_STATE: Record<string, { t: string; bad: boolean }> = {
  spreman: { t: "L-PFR spreman", bad: false },
  trebaPin: { t: "L-PFR traži PIN", bad: true },
  nemaKartice: { t: "L-PFR ne vidi karticu u čitaču", bad: true },
  nijeDostupan: { t: "L-PFR nije dostupan", bad: true },
  nijePodesen: { t: "L-PFR nije podešen", bad: true },
  blokiran: { t: "Kartica je zaključana", bad: true },
};

/** Podešavanja → Kase i uređaji: sve što se prijavilo u HAFiskal (telefoni, tableti, kase na računaru). */
export function DevicesTab() {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const devices = useQuery({ queryKey: ["my-devices"], queryFn: () => api.listMyDevices(), refetchInterval: 30000 });

  const fail = (e: unknown) => setErr(e instanceof ApiError ? String(e.response.message) : "Izmena nije uspela.");
  const refresh = () => {
    setErr(null);
    qc.invalidateQueries({ queryKey: ["my-devices"] });
  };
  const block = useMutation({
    mutationFn: (v: { id: string; blocked: boolean }) => api.setMyDeviceBlocked(v.id, v.blocked),
    onSuccess: refresh,
    onError: fail,
  });
  const mod = useMutation({
    mutationFn: (v: { id: string; key: string; allowed: boolean }) => api.setMyDeviceModule(v.id, v.key, v.allowed),
    onSuccess: refresh,
    onError: fail,
  });

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <h2 className="text-sm font-semibold text-slate-800">Kase i uređaji</h2>
        <p className="mt-1 text-xs text-slate-500">
          Ovde vidite sve uređaje na kojima je neko prijavljen u HAFiskal: telefone, tablete i kase na računaru. Uređaj se
          pojavi sam čim se na njemu prijavite. Za svaki možete da odredite šta sme da koristi, ili da ga isključite (npr. ako
          je izgubljen).
        </p>
      </div>

      {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}

      {devices.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : devices.isError ? (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">Učitavanje uređaja nije uspelo.</p>
      ) : (devices.data ?? []).length === 0 ? (
        <EmptyState
          icon={Laptop}
          title="Još nema prijavljenih uređaja"
          description="Prijavite se u HAFiskal kasu na telefonu, tabletu ili računaru i uređaj će se pojaviti ovde."
        />
      ) : (
        (devices.data ?? []).map((d: CompanyDeviceDto) => (
          <div key={d.id} className={`rounded-xl border bg-surface p-4 shadow-subtle ${d.blocked ? "border-red-200" : "border-border"}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <PlatformIcon p={d.platform} />
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    {d.name || platformLabel(d.platform)}
                    {d.blocked && <span className="ml-2 rounded bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">Isključen</span>}
                  </div>
                  <div className="text-xs text-slate-500">
                    {platformLabel(d.platform)} · {d.userName} · viđen {lastSeen(d.lastActiveAt)}
                    {d.appVersion ? ` · verzija ${d.appVersion}` : ""}
                  </div>
                </div>
              </div>
              <Button
                size="sm"
                variant="secondary"
                disabled={block.isPending}
                onClick={() => {
                  if (!d.blocked && !confirm("Isključiti ovaj uređaj? Neće moći da radi dok ga ponovo ne uključite.")) return;
                  block.mutate({ id: d.id, blocked: !d.blocked });
                }}
              >
                {d.blocked ? "Uključi ponovo" : "Isključi"}
              </Button>
            </div>

            {d.lpfr?.state && (() => {
              const st = LPFR_STATE[d.lpfr.state] ?? { t: d.lpfr.state, bad: true };
              return (
                <div className={`mt-3 rounded-lg px-3 py-2 text-xs ${st.bad ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                  <b>{st.t}</b>
                  {st.bad && " — sa ovom kasom se trenutno ne može izdati račun preko L-PFR-a."}
                  {d.lpfr.message && <div>{d.lpfr.message}</div>}
                  <div className="text-slate-500">
                    {d.lpfr.jid ? `Kartica ${d.lpfr.jid}` : ""}
                    {d.lpfr.softwareVersion ? ` · L-PFR ${d.lpfr.softwareVersion}` : ""}
                    {d.lpfr.lastInvoiceNumber ? ` · poslednji račun ${d.lpfr.lastInvoiceNumber}` : ""}
                    {d.lpfr.checkedAt ? ` · provereno ${lastSeen(d.lpfr.checkedAt)}` : ""}
                  </div>
                </div>
              );
            })()}

            {!d.blocked && (
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                {d.modules.length === 0 ? (
                  <span className="text-xs text-slate-400">Firma nema module koji se dodeljuju uređajima.</span>
                ) : (
                  d.modules.map((m) => (
                    <label key={m.featureKey} className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={m.allowed}
                        disabled={mod.isPending}
                        onChange={(e) => mod.mutate({ id: d.id, key: m.featureKey, allowed: e.target.checked })}
                      />
                      {m.name}
                    </label>
                  ))
                )}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
